import axios from 'axios';
import { Fragment, useState } from 'react';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { ApiDeliveryAddress, DeliveryTypeCompany } from '../../types';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import { Badge } from '../Badge/Badge';
import { ContentCard } from '../ContentCard/ContentCard';
import { AddressSummary } from '../Form/AddressSummary';
import { Button } from '../Form/Button';
import { DeliveryAddressInput } from '../Form/DeliveryAddressInput';
import { useFormValidationContainer } from '../Form/FormValidatorContainer';
import { onKeyPress } from '../Form/PressedEnter';
import { hashCode, renderValue } from '../Form/utils';
import { Icon, close } from '../Icon/Icon';
import { Modal } from '../Modal/Modal';
import './AccountDeliveryAddresses.css';

const axiosConf = { withCredentials: true };

type PatchOp = { op: string; path: string; value: string | number | boolean };
type DeliveryAddressPayload = Omit<ApiDeliveryAddress, 'id' | 'primary' | 'lastUsed'>;

function buildPatch<T extends { [key: string]: string | number }>(original: T, changed: T): PatchOp[] {
	return Object.keys(original)
		.filter((key) => changed[key] !== original[key] && changed[key] !== undefined)
		.map((key) => ({
			op: 'replace',
			path: '/' + key,
			value: changed[key]!,
		}));
}

type AccountDeliveryAddressesProps = {
	deliveryAddresses: ApiDeliveryAddress[];
	mutateDeliveryAddresses: () => void;
};

export const AccountDeliveryAddresses = ({
	deliveryAddresses,
	mutateDeliveryAddresses,
}: AccountDeliveryAddressesProps): JSX.Element => {
	const t = useTranslation().accountPage;
	const tCheckout = useTranslation().checkout;
	const tForm = useTranslation().form;
	const tGeneric = useTranslation().generic;
	const { api } = useConfig();
	const form = useFormValidationContainer();
	const deliveryAlerts = useAlerts();
	const [deliveryFormOpen, setDeliveryFormOpen] = useState(false);
	const [editingDeliveryAddress, setEditingDeliveryAddress] = useState<ApiDeliveryAddress | null>(null);
	const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState('');
	const deliveryType: DeliveryTypeCompany = 'door_delivery';

	function buildDeliveryAddressPayload(): DeliveryAddressPayload {
		return {
			company: form.deliveryCompany.value,
			careOf: form.deliveryCareOf.value,
			firstname: form.deliveryFirstname.value,
			lastname: form.deliveryLastname.value,
			street: form.deliveryStreet.value,
			doorCode: form.deliveryDoorCode.value,
			city: form.deliveryCity.value,
			zip: form.deliveryZip.value,
			phone: form.deliveryPhone.value,
		};
	}

	function mapDeliveryAddressToPayload(address: ApiDeliveryAddress): DeliveryAddressPayload {
		return {
			company: address.company,
			careOf: address.careOf,
			firstname: address.firstname,
			lastname: address.lastname,
			street: address.street,
			doorCode: address.doorCode,
			city: address.city,
			zip: address.zip,
			phone: address.phone,
		};
	}

	async function sendPatch(endpoint: string, payload: unknown, successMessage: string): Promise<boolean> {
		deliveryAlerts.clear();
		try {
			await axios.patch(`${api}${endpoint}`, payload, axiosConf);
			deliveryAlerts.set({ type: 'success', message: successMessage });
			return true;
		} catch (e) {
			deliveryAlerts.set({ type: 'error', message: t.couldNotUpdateInfo });
			return false;
		}
	}

	async function sendPost(endpoint: string, payload: unknown, successMessage: string): Promise<boolean> {
		deliveryAlerts.clear();
		try {
			await axios.post(`${api}${endpoint}`, payload, axiosConf);
			deliveryAlerts.set({ type: 'success', message: successMessage });
			return true;
		} catch (e) {
			deliveryAlerts.set({ type: 'error', message: t.couldNotUpdateInfo });
			return false;
		}
	}

	async function sendDelete(endpoint: string, successMessage: string): Promise<boolean> {
		deliveryAlerts.clear();
		try {
			await axios.delete(`${api}${endpoint}`, axiosConf);
			deliveryAlerts.set({ type: 'success', message: successMessage });
			return true;
		} catch (e) {
			deliveryAlerts.set({ type: 'error', message: t.couldNotUpdateInfo });
			return false;
		}
	}

	function resetDeliveryFormState() {
		form.resetAddressForm();
		setEditingDeliveryAddress(null);
		setSelectedDeliveryAddress('');
	}

	function handleAddDeliveryAddress() {
		deliveryAlerts.clear();
		resetDeliveryFormState();
		setDeliveryFormOpen(true);
	}

	function handleEditDeliveryAddress(address: ApiDeliveryAddress) {
		deliveryAlerts.clear();
		setEditingDeliveryAddress(address);
		setSelectedDeliveryAddress(address.id !== undefined ? String(address.id) : '');
		form.fillAddressForm(address);
		setDeliveryFormOpen(true);
	}

	function handleCancelDeliveryAddress() {
		deliveryAlerts.clear();
		resetDeliveryFormState();
		setDeliveryFormOpen(false);
	}

	async function submitDeliveryAddress() {
		deliveryAlerts.clear();
		if (!form.validateDeliveryAddress({ deliveryType })) {
			deliveryAlerts.set({ type: 'error', message: t.allFieldsNotCorrectlyFilled });
			return;
		}

		const payload = buildDeliveryAddressPayload();
		let success = false;

		if (editingDeliveryAddress) {
			if (editingDeliveryAddress.id === undefined) {
				deliveryAlerts.set({ type: 'error', message: t.couldNotUpdateInfo });
				return;
			}
			const originalPayload = mapDeliveryAddressToPayload(editingDeliveryAddress);
			const patch = buildPatch(originalPayload, payload);
			if (patch.length === 0) {
				deliveryAlerts.set({ type: 'success', message: t.informationUpdated });
				success = true;
			} else {
				success = await sendPatch(
					`me/delivery-addresses/${editingDeliveryAddress.id}.json`,
					patch,
					t.informationUpdated
				);
			}
		} else {
			success = await sendPost('me/delivery-addresses.json', payload, t.informationUpdated);
		}

		if (success) {
			mutateDeliveryAddresses();
			resetDeliveryFormState();
			setDeliveryFormOpen(false);
		}
	}

	async function deleteDeliveryAddress(address: ApiDeliveryAddress) {
		if (address.id === undefined) {
			deliveryAlerts.set({ type: 'error', message: t.couldNotUpdateInfo });
			return;
		}

		const shouldDelete = window.confirm(t.deleteConfirm);
		if (!shouldDelete) return;

		const success = await sendDelete(`me/delivery-addresses/${address.id}.json`, t.informationUpdated);
		if (success) {
			mutateDeliveryAddresses();
			if (editingDeliveryAddress?.id === address.id) {
				handleCancelDeliveryAddress();
			}
		}
	}

	async function setPrimaryDeliveryAddress(address: ApiDeliveryAddress, makePrimary: boolean) {
		if (address.id === undefined) {
			deliveryAlerts.set({ type: 'error', message: t.couldNotUpdateInfo });
			return;
		}

		const patch: PatchOp[] = [{ op: 'replace', path: '/primary', value: makePrimary ? 1 : 0 }];
		const success = await sendPatch(`me/delivery-addresses/${address.id}.json`, patch, t.informationUpdated);
		if (success) {
			mutateDeliveryAddresses();
		}
	}

	return (
		<div style={{ marginBottom: '1.6rem' }}>
			<ContentCard title={tCheckout.deliveryAddress}>
				<AlertMessages alerts={deliveryAlerts} />
				{deliveryAddresses.length > 0 && (
					<div className="account-delivery-addresses">
						{deliveryAddresses.map((address) => {
							const addressKey = address.id ?? hashCode(renderValue(address) || '');
							const hasId = address.id !== undefined;
							const isPrimary = Boolean(address.primary);
							return (
								<Fragment key={addressKey}>
									<div className="account-delivery-address-row">
										<div className="account-delivery-address-summary">
											{isPrimary && (
												<Badge size="small" color="green">
													<span
														style={{
															display: 'inline-flex',
															alignItems: 'center',
															gap: '0.35rem',
														}}
													>
														{t.primary}
													</span>
												</Badge>
											)}
											<AddressSummary address={address} />
										</div>
										<div className="account-delivery-address-actions">
											<Button
												type="button"
												className="secondary"
												disabled={!hasId}
												onClick={() => setPrimaryDeliveryAddress(address, !isPrimary)}
											>
												{isPrimary ? t.setNotPrimary : t.setPrimary}
											</Button>
											<Button
												type="button"
												className="secondary"
												disabled={!hasId}
												onClick={() => deleteDeliveryAddress(address)}
											>
												{tGeneric.remove}
											</Button>
											<Button
												type="button"
												className="secondary"
												disabled={!hasId}
												onClick={() => handleEditDeliveryAddress(address)}
											>
												{tGeneric.edit}
											</Button>
										</div>
									</div>
								</Fragment>
							);
						})}
					</div>
				)}
				{deliveryFormOpen ? (
					<Modal
						className="content-card-modal account-delivery-address-modal"
						show={deliveryFormOpen}
						onDismiss={handleCancelDeliveryAddress}
						toggle={handleCancelDeliveryAddress}
					>
						<ContentCard className="content account-delivery-address-modal-content">
							<div className="account-delivery-address-modal-header">
								<h2 className="account-delivery-address-modal-title">
									{editingDeliveryAddress ? tGeneric.edit : tForm.addNewAddress}
								</h2>
								<button className="close-btn" onClick={handleCancelDeliveryAddress} type="button">
									<Icon shape={{ ...close, fill: 'black' }} size={20} />
								</button>
							</div>
							<div className="account-delivery-address-form">
								<DeliveryAddressInput
									deliveryType={deliveryType}
									deliveryAddresses={[]}
									selectedAddress={selectedDeliveryAddress}
									setSelectedAddress={setSelectedDeliveryAddress}
									onKeyPress={onKeyPress}
								/>
							</div>
							<div className="card-footer">
								<Button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										submitDeliveryAddress();
									}}
								>
									{tGeneric.save}
								</Button>
								<Button type="button" className="secondary" onClick={handleCancelDeliveryAddress}>
									{tGeneric.cancel}
								</Button>
							</div>
						</ContentCard>
					</Modal>
				) : (
					<Button type="button" className="secondary" onClick={handleAddDeliveryAddress}>
						{tForm.addNewAddress}
					</Button>
				)}
			</ContentCard>
		</div>
	);
};
